import { TypedAnalyticsEvent } from "@omni/types/components/tracking/analyticsEvent";
import { CheckoutToolStepTrackingInfo } from "@omni/types/components/tracking/tracking-info/tools/toolStep.trackingInfo";

import { GoogleAnalyticsEvents } from "../GoogleAnalyticsEvents";
import {
    GoogleAnalyticsMinimumDataset,
    getPageInfo,
    getUserInfo
} from "../GoogleAnalyticsMinimumDataset";
import {
    GoogleAnalyticsProduct,
    getGoogleAnalyticsProduct
} from "./GoogleAnalyticsProduct";

interface GoogleAnalyticsCheckoutStep extends GoogleAnalyticsMinimumDataset {
    event: string;
    ecommerce: {
        checkout: {
            actionField: {
                step: number;
                option?: string;

                name?: string; // non google variable
                type?: string; // non google variable
                placement?: string; // non google variable
                stepname?: string; // non google variable
                result?: any; // non google variable
                discount?: any; // non google variable
            };
            products: GoogleAnalyticsProduct[];
        };
    };
}

interface GoogleAnalyticsCheckoutPurchase
    extends GoogleAnalyticsMinimumDataset {
    event: string;
    ecommerce: {
        purchase: {
            actionField: {
                id?: string; // transactionId
                revenue?: number; // Total transaction value (incl. tax and shipping)
                tax?: string; // The total tax associated with the transaction.
                shipping?: string; // The shipping cost associated with the transaction.
                coupon?: string; // The transaction coupon redeemed with the transaction.
                list?: string; // The list that the associated products belong to. Optional.
                step?: number; // A number representing a step in the checkout process. Optional on `checkout` actions.
                option?: string; // Additional field for checkout and checkout_option actions that can describe option information on the checkout page, like selected payment method.

                name?: string; // non google variable
                type?: string; // non google variable
                placement?: string; // non google variable
                stepname?: string; // non google variable
                result?: any; // non google variable
                discount?: any; // non google variable
            };
            products: GoogleAnalyticsProduct[];
        };
    };
}

const getGoogleAnalyticsCheckoutPurchase = (
    data: TypedAnalyticsEvent<CheckoutToolStepTrackingInfo>
): GoogleAnalyticsCheckoutPurchase => {
    return {
        event: GoogleAnalyticsEvents.CheckoutPurchase,
        ecommerce: {
            purchase: {
                actionField: {
                    id: data.payload.eventData.cart?.orderId,
                    revenue: data.payload.eventData.cart?.totalPrice,
                    coupon: data.payload.eventData.cart?.promotions
                        ?.map((promo: any) => promo.id)
                        .join(","),
                    step: data.payload.eventData.stepNumber,
                    option: data.payload.eventData.option,
                    name: data.payload.eventData.name,
                    type: data.payload.eventData.type,
                    placement: data.payload.eventData.placement,
                    stepname: data.payload.eventData.stepName,
                    result: data.payload.eventData.result,
                    discount: data.payload.eventData.cart?.globalDiscounts
                },
                products: (data.payload.eventData?.cart?.items || []).map(
                    (item) => {
                        return {
                            ...getGoogleAnalyticsProduct(item),
                            quantity: item.quantity
                        };
                    }
                )
            }
        },
        pageInfo: getPageInfo(data),
        userInfo: getUserInfo(data)
    };
};

const getGoogleAnalyticsCheckoutStep = (
    data: TypedAnalyticsEvent<CheckoutToolStepTrackingInfo>
): GoogleAnalyticsCheckoutStep => {
    return {
        event: GoogleAnalyticsEvents.CheckoutStep,
        ecommerce: {
            checkout: {
                actionField: {
                    step: data.payload.eventData.stepNumber,
                    option: data.payload.eventData.option,

                    name: data.payload.eventData.name,
                    type: data.payload.eventData.type,
                    placement: data.payload.eventData.placement,
                    stepname: data.payload.eventData.stepName,
                    result: data.payload.eventData.result
                },
                products: (data.payload.eventData?.cart?.items || []).map(
                    (item) => {
                        return {
                            ...getGoogleAnalyticsProduct(item),
                            quantity: item.quantity
                        };
                    }
                )
            }
        },
        pageInfo: getPageInfo(data),
        userInfo: getUserInfo(data)
    };
};

export { getGoogleAnalyticsCheckoutPurchase, getGoogleAnalyticsCheckoutStep };
